import { useState, useRef, useEffect } from "react"
import Window from "../Window/Window"
import { MagnifyingGlass, ArrowLeft, ArrowRight, ArrowClockwise } from "phosphor-react"
import styles from "./FaqWindow.module.css"
import FaqResultView from "./FaqResultView"
import { translations } from "../../translations"
import { useTranslationUpdate } from "../../hooks/useTranslationUpdate"

type Props = {
  onClose: () => void
  isMinimized: boolean
  onToggleMinimize: () => void
}

type FaqData = {
  question: string
  answer: string
}

const getFaqsData = (): FaqData[] => [
  {
    question: translations('faq.setup_question'),
    answer: translations('faq.setup_answer')
  },
  {
    question: translations('faq.contact_question'),
    answer: translations('faq.contact_answer')
  },
  {
    question: translations('faq.languages_question'),
    answer: translations('faq.languages_answer')
  },
  {
    question: translations('faq.portfolio_question'),
    answer: translations('faq.portfolio_answer')
  },
]

export default function FaqWindow({ onClose, isMinimized, onToggleMinimize }: Props) {
  const [searchValue, setSearchValue] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [selectedFaq, setSelectedFaq] = useState<FaqData | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useTranslationUpdate()
  
  const faqsData = getFaqsData()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSearchClick = () => {
    setIsOpen(true)
    inputRef.current?.focus()
  }

  const handleItemClick = (faq: FaqData) => {
    setSearchValue(faq.question)
    setIsOpen(false)
    setSelectedFaq(faq)
    setShowResults(true)
  }

  const handleSelectFaq = (faq: FaqData) => {
    setSelectedFaq(faq)
  }

  const handleBackToSearch = () => {
    setShowResults(false)
    setSelectedFaq(null)
  }

  const filteredFaqs = faqsData.filter(faq => 
    faq.question.toLowerCase().includes(searchValue.toLowerCase())
  )

  if (isMinimized) return null

  return (
    <Window
      title={translations('faq.title')}
      onClose={onClose}
      isMinimized={isMinimized}
      onToggleMinimize={onToggleMinimize}
      initialPosition={{ x: 200, y: 60 }}
      initialSize={{ width: 800, height: 600 }}
      minSize={{ width: 600, height: 400 }}
      bodyClassName={styles.body}
      containerClassName={styles.poppinsAll}
    >
      <div className={styles.navBar}>
        <button 
          className={styles.navBtn} 
          onClick={showResults ? handleBackToSearch : undefined}
          aria-label="Voltar"
          style={{ cursor: showResults ? 'pointer' : 'default' }}
        >
          <ArrowLeft size={20} weight="regular" />
        </button>
        <button className={styles.navBtn} aria-label="Avançar">
          <ArrowRight size={20} weight="regular" />
        </button>
        <button className={styles.navBtn} aria-label="Recarregar">
          <ArrowClockwise size={20} weight="regular" />
        </button>
        <div className={styles.urlBar}>
          <MagnifyingGlass size={16} color="#5f6368" />
          <span className={styles.urlText}>
            {showResults && selectedFaq 
              ? `${translations('faq.url_search')}${encodeURIComponent(selectedFaq.question)}`
              : translations('faq.url_home')
            }
          </span>
        </div>
      </div>

      {showResults && selectedFaq ? (
        <FaqResultView 
          selectedFaq={selectedFaq}
          faqsData={faqsData}
          onSelectFaq={handleSelectFaq}
        />
      ) : (
        <>
          <div className={styles.headerBand}>
            <h1 className={styles.title}>
              <span style={{ color: '#4285F4' }}>F</span>
              <span style={{ color: '#DB4437' }}>r</span>
              <span style={{ color: '#F4B400' }}>e</span>
              <span style={{ color: '#4285F4' }}>q</span>
              <span style={{ color: '#0F9D58' }}>u</span>
              <span style={{ color: '#DB4437' }}>e</span>
              <span style={{ color: '#4285F4' }}>n</span>
              <span style={{ color: '#F4B400' }}>t</span>
              <span style={{ color: '#0F9D58' }}>l</span>
              <span style={{ color: '#DB4437' }}>y</span>
              {' '}
              <span style={{ color: '#4285F4' }}>a</span>
              <span style={{ color: '#F4B400' }}>s</span>
              <span style={{ color: '#DB4437' }}>k</span>
              <span style={{ color: '#0F9D58' }}>e</span>
              <span style={{ color: '#4285F4' }}>d</span>
              {' '}
              <span style={{ color: '#DB4437' }}>q</span>
              <span style={{ color: '#F4B400' }}>u</span>
              <span style={{ color: '#0F9D58' }}>e</span>
              <span style={{ color: '#4285F4' }}>s</span>
              <span style={{ color: '#DB4437' }}>t</span>
              <span style={{ color: '#F4B400' }}>i</span>
              <span style={{ color: '#0F9D58' }}>o</span>
              <span style={{ color: '#4285F4' }}>n</span>
              <span style={{ color: '#DB4437' }}>s</span>
            </h1>

            <div className={styles.searchWrapper} ref={wrapperRef}>
              <div 
                className={`${styles.searchContainer} ${isOpen ? styles.searchContainerOpen : ''}`}
                onClick={handleSearchClick}
              >
                <MagnifyingGlass size={20} color="#666" />
                <input
                  ref={inputRef}
                  className={styles.searchInput}
                  placeholder=""
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={() => setIsOpen(true)}
                />
              </div>

              {isOpen && (
                <div className={styles.dropdown}>
                  {filteredFaqs.map((faq, index) => (
                    <button
                      key={index}
                      className={styles.dropdownItem}
                      onClick={() => handleItemClick(faq)}
                    >
                      {faq.question}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.sectionGrid}>
            <div className={styles.socialLinks}>
              <button className={styles.socialIcon} aria-label="GitHub" onClick={() => window.open('https://github.com/evelynrporto', '_blank')}>
                <img src="/images/social_medias/github.svg" alt="GitHub" />
              </button>
              <button className={styles.socialIcon} aria-label="Instagram" onClick={() => window.open('https://www.instagram.com/evelyn_porto', '_blank')}>
                <img src="/images/social_medias/instagram.svg" alt="Instagram" />
              </button>
              <button className={styles.socialIcon} aria-label="LinkedIn" onClick={() => window.open('https://www.linkedin.com/in/evelyn-porto-85925a219/', '_blank')}>
                <img src="/images/social_medias/linkedin.svg" alt="LinkedIn" />
              </button>
            </div>
          </div>
        </>
      )}
    </Window>
  )
}